"use client"

import { Suspense } from "react"
import { useState, useEffect, useRef } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import Image from "next/image"
import ProfileGridCard from "@/components/profile/profile-grid-card"
import ProfileListCard from "@/components/profile/profile-list-card"
import { ProfileListCardSkeleton, ProfileGridCardSkeleton } from "@/components/profile/profile-skeleton"
import SearchAutocomplete from "@/components/search/search-autocomplete"
import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import Link from "next/link"
import Navbar from "../navbar"
import Footer from "../footer"
import sectorsData from "@/src/data/sectors-data.json"
import bcManifest from "@/src/data/bc-manifest.json"
import { fetchHomepageProfiles } from "@/lib/api/homepage-profiles"
import type { PublicProfile } from "@/lib/types/public-profile"
import { useSavedSearches } from "@/hooks/use-saved-searches"
import StickyJoinCTA from "@/src/components/bc/StickyJoinCTA"
import InlineJoinCTA from "@/src/components/bc/InlineJoinCTA"
import WhyBoardChampionsSection from "./why-board-champions-section"
import WaveDivider from "@/components/ui/wave-divider"
import HomepageTestimonials from "@/components/homepage-testimonials"

interface TypeItem {
  label: string
  href: string
  children: { label: string; href: string }[]
}

interface NavigationItem {
  name: string
  href?: string
  isActive?: boolean
  hasDropdown?: boolean
}

type ViewMode = 'sectors' | 'profiles' | 'profile-detail'

interface Profile extends PublicProfile {}

function HomePageContent() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [openType, setOpenType] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isPaused, setIsPaused] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('sectors')
  const [profilesViewMode, setProfilesViewMode] = useState<"grid" | "list">("list")
  const [selectedSector, setSelectedSector] = useState<string | null>(null)
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null)
  const [filteredProfiles, setFilteredProfiles] = useState<Profile[]>([])
  const [allProfiles, setAllProfiles] = useState<Profile[]>([])
  const [isLoadingProfiles, setIsLoadingProfiles] = useState<boolean>(true)
  const [profilesError, setProfilesError] = useState<string | null>(null)
  const [emptyFacets, setEmptyFacets] = useState<any | null>(null)
  const [loadingFacets, setLoadingFacets] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<'recent' | 'experience' | 'relevance'>('recent')
  const [totalProfiles, setTotalProfiles] = useState(0)
  const saved = useSavedSearches()
  const lastScrolledSector = useRef<string | null>(null)

  // Pagination settings
  const PROFILES_PER_PAGE = 12

  // Helpers
  const slugify = (value: string) => value.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  const deslugify = (slug: string) => {
    const acronyms = new Set([
      'ceo','cfo','cmo','coo','cco','cto','cio','chro','cpo','cdo','cro','cso','ciso','md','ned','pr','ai','hr','it'
    ])
    return slug.split('-').map(s => {
      const lower = s.toLowerCase()
      if (acronyms.has(lower)) return lower.toUpperCase()
      return s.charAt(0).toUpperCase() + s.slice(1)
    }).join(' ')
  }

  const updateUrl = (params: Record<string, string | string[] | null | undefined>, replace: boolean = false) => {
    const url = new URL(typeof window !== 'undefined' ? window.location.href : `http://localhost${pathname}`)
    const keysToClear: string[] = []
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
        keysToClear.push(key)
      } else if (Array.isArray(value)) {
        url.searchParams.set(key, value.join(','))
      } else {
        url.searchParams.set(key, value)
      }
    })
    keysToClear.forEach(k => url.searchParams.delete(k))
    const next = url.pathname + (url.search ? `?${url.searchParams.toString()}` : '')
    if (replace) router.replace(next, { scroll: false })
    else router.push(next, { scroll: false })
  }

  const applyFiltersFromParams = async (params: URLSearchParams, skipFiltering: boolean = false, profilesOverride?: Profile[]) => {
    const q = params.get('q') || params.get('query') || ''
    const roleParam = params.get('role') || ''
    const sectorsParam = params.get('sectors') || ''
    const profileId = params.get('profile') || ''
    const view = params.get('view') || ''
    const pageParam = parseInt(params.get('page') || '1', 10)
    const sortByParam = (params.get('sortBy') || 'recent') as 'recent' | 'experience' | 'relevance'

    // Update pagination and sorting state
    if (pageParam !== currentPage) setCurrentPage(pageParam)
    if (sortByParam !== sortBy) setSortBy(sortByParam)

    // Apply search query to input
    if (q !== searchQuery) setSearchQuery(q)

    // If profile is specified, prefer detail view
    if (profileId) {
      // Try to find locally first
      const found = (profilesOverride || allProfiles).find(p => p.id === profileId)
      if (found) {
        setSelectedProfile(found)
        setViewMode('profile-detail')
      } else {
        // Fetch by id
        try {
          const res = await fetch(`/api/search/profiles/${profileId}`, { cache: 'no-store' })
          if (res.ok) {
            const json = await res.json()
            // Map response to PublicProfile shape
            const mapped: Profile = {
              id: json?.data?.id || profileId,
              name: json?.data?.name || 'Executive',
              title: json?.data?.title || 'Executive',
              category: 'executive',
              sectors: json?.data?.sectors || json?.data?.industryExpertise || [],
              image: json?.data?.imageUrl || null,
              bio: json?.data?.bio || json?.data?.summary || '',
              experience: json?.data?.experience || '—',
              location: json?.data?.location || '—',
              skills: json?.data?.skills || json?.data?.keySkills || [],
              availability: json?.data?.availability || '—',
            }
            setSelectedProfile(mapped)
            setViewMode('profile-detail')
          } else {
            // If fetch fails, fall back to profiles view
            setViewMode('profiles')
          }
        } catch (_) {
          setViewMode('profiles')
        }
      }
      return
    }

    // Build filtered list if we have profiles loaded
    const profilesToFilter = profilesOverride || allProfiles
    let nextFiltered = profilesToFilter
    let anyFilter = false

    // If skipFiltering is true, we already have filtered profiles from API
    if (skipFiltering && (sectorsParam || roleParam)) {
      anyFilter = true
      nextFiltered = profilesToFilter // Use all profiles since they're already filtered from API
    } else if (q) {
      const qLower = q.toLowerCase()
      nextFiltered = nextFiltered.filter(profile => 
        profile.name.toLowerCase().includes(qLower) ||
        profile.title.toLowerCase().includes(qLower) ||
        profile.bio.toLowerCase().includes(qLower) ||
        profile.skills.some(skill => skill.toLowerCase().includes(qLower))
      )
      anyFilter = true
    }

    if (!skipFiltering && roleParam) {
      const roleSlug = roleParam.toLowerCase()
      const readable = deslugify(roleSlug)
      setSelectedSector(readable)

      // Use canonical browse-types mapping for robust matching
      try {
        const { getBrowseTypeBySlug } = await import('@/src/data/browse-types')
        const { profileMatchesSector } = await import('@/lib/config/sector-mappings')
        const type = getBrowseTypeBySlug(roleSlug)
        
        if (type) {
          const roleTerms = (type.filters.role || []).map(r => r.toLowerCase())
          const sectorTerms = (type.filters.sectors || []).map(s => s.toLowerCase())
          
          // Filter by both roles AND sectors if both are present
          nextFiltered = nextFiltered.filter(profile => {
            let matchesRole = false
            let matchesSector = false
            
            // Check role matching
            if (roleTerms.length > 0) {
              const profileRoles = (profile.roles || []).map(r => r.toLowerCase())
              const titleLower = (profile.title || '').toLowerCase()
              
              matchesRole = roleTerms.some(term => 
                profileRoles.includes(term) || titleLower.includes(term)
              )
            }
            
            // Check sector matching
            if (sectorTerms.length > 0) {
              matchesSector = sectorTerms.some(sector => 
                profileMatchesSector(profile.sectors, sector, profile.roles)
              )
            }
            
            // If both filters exist, require at least one match
            // If only one exists, require that one
            if (roleTerms.length > 0 && sectorTerms.length > 0) {
              return matchesRole || matchesSector
            } else if (roleTerms.length > 0) {
              return matchesRole
            } else if (sectorTerms.length > 0) {
              return matchesSector
            }
            
            return true // No filters, include all
          })
        } else {
          // Fallback: no mapping found
          nextFiltered = nextFiltered.filter(profile =>
            (profile.title || '').toLowerCase().includes(readable.toLowerCase()) ||
            (profile.roles || []).some(role => role.toLowerCase().includes(readable.toLowerCase()))
          )
        }
      } catch {
        // Fallback if mapping import fails
        nextFiltered = nextFiltered.filter(profile =>
          (profile.title || '').toLowerCase().includes(readable.toLowerCase()) ||
          (profile.roles || []).some(role => role.toLowerCase().includes(readable.toLowerCase()))
        )
      }
      anyFilter = true
    } else {
      setSelectedSector(null)
    }

    if (!skipFiltering && sectorsParam) {
      const { profileMatchesSector } = await import('@/lib/config/sector-mappings')
      const sectors = sectorsParam.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
      if (sectors.length) {
        nextFiltered = nextFiltered.filter(profile => 
          sectors.some(sector => profileMatchesSector(profile.sectors, sector, profile.roles))
        )
        anyFilter = true
      }
    }

    setFilteredProfiles(nextFiltered)

    // If view is explicitly set in URL, use it
    if (view === 'profiles') {
      console.log('[applyFiltersFromParams] Setting view mode to profiles')
      setViewMode('profiles')
      return
    }

    if (view === 'sectors') {
      console.log('[applyFiltersFromParams] Setting view mode to sectors')
      setViewMode('sectors')
      return
    }

    // Otherwise, show profiles if we have any filters, sectors otherwise
    const newMode = anyFilter ? 'profiles' : 'sectors'
    console.log('[applyFiltersFromParams] Setting view mode to:', newMode, 'anyFilter:', anyFilter)
    setViewMode(newMode)
    setSelectedProfile(null)
  }
  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoadingProfiles(true)
      setProfilesError(null)
      console.log('[Homepage] Fetching profiles...')
      // Read pagination and sorting from URL
      const roleParam = searchParams?.get('role') || ''
      const sectorsParam = searchParams?.get('sectors') || ''
      const pageParam = parseInt(searchParams?.get('page') || '1', 10)
      const sortByParam = (searchParams?.get('sortBy') || 'recent') as 'recent' | 'experience' | 'relevance'

      let profiles: PublicProfile[] = []
      let error: string | undefined
      let total = 0

      try {
        if (roleParam || sectorsParam) {
          const url = new URL('/api/search/candidates', typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
          url.searchParams.set('limit', String(PROFILES_PER_PAGE))
          url.searchParams.set('page', String(pageParam))
          url.searchParams.set('sortBy', sortByParam)
          if (roleParam) url.searchParams.set('role', roleParam)
          if (sectorsParam) url.searchParams.set('sectors', sectorsParam)
          const res = await fetch(url.toString(), { cache: 'no-store' })
          if (res.ok) {
            const json = await res.json()
            const raw = (json?.data?.profiles ?? []) as any[]
            total = json?.data?.pagination?.total ?? raw.length
            // map to PublicProfile shape
            const { mapSearchApiProfilesToPublicProfiles, filterActiveCompletedProfiles } = await import('@/lib/utils/profile-mapper')
            const filteredRaw = filterActiveCompletedProfiles(raw)
            profiles = mapSearchApiProfilesToPublicProfiles(filteredRaw)
          } else {
            error = `Failed to load filtered profiles (${res.status})`
          }
        } else {
          // For homepage without filters, load all profiles for client-side filtering
          // This maintains existing behavior for sector grid selection
          const res = await fetchHomepageProfiles(100)
          profiles = res.profiles
          error = res.error
          total = profiles.length
        }
      } catch (e: any) {
        error = e?.message || 'Failed to load profiles'
      }
      if (cancelled) return
      if (error) {
        console.log('[Homepage] Error loading profiles:', error)
        setProfilesError(error)
        setAllProfiles([])
        setFilteredProfiles([])
        setTotalProfiles(0)
      } else {
        console.log('[Homepage] Loaded profiles:', profiles.length)
        console.log('[Homepage] Sectors param:', sectorsParam, 'Role param:', roleParam)
        const pablo = profiles.find(p => p.name === 'Pablo Garner')
        if (pablo) {
          console.log('[Homepage] Pablo found in loaded profiles:', {
            name: pablo.name,
            roles: pablo.roles,
            title: pablo.title
          })
        } else {
          console.log('[Homepage] Pablo NOT found in loaded profiles')
        }
        setAllProfiles(profiles)
        setTotalProfiles(total)
        // When we've already filtered via API, just set the filtered profiles directly
        if (sectorsParam || roleParam) {
          setFilteredProfiles(profiles)
          // Still need to update view mode based on URL
          const view = searchParams?.get('view') || ''
          if (view === 'profiles' || sectorsParam || roleParam) {
            setViewMode('profiles')
          }
        } else {
          // No API filters, apply client-side filters
          setFilteredProfiles(profiles)
          if (searchParams) {
            applyFiltersFromParams(searchParams, false, profiles)
          }
        }
      }
      setIsLoadingProfiles(false)
    }
    load()
    return () => { cancelled = true }
  }, [searchParams])

  // Scroll to "Why Board Champions?" section when URL has sector/role filters
  useEffect(() => {
    const sectorsParam = searchParams?.get('sectors')
    const roleParam = searchParams?.get('role')
    const profileParam = searchParams?.get('profile')

    // Don't scroll if viewing specific profile or no filters present
    if (profileParam || (!sectorsParam && !roleParam)) {
      // Reset scroll tracking when clearing filters
      lastScrolledSector.current = null
      return
    }

    // Get current sector identifier for tracking
    const currentSector = sectorsParam?.split(',')[0] || roleParam

    // Only scroll if this is a different sector than we last scrolled for
    if (lastScrolledSector.current === currentSector) return

    // Wait for profiles to load and viewMode to switch to 'profiles'
    if (!isLoadingProfiles && viewMode === 'profiles') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const section = document.getElementById('why-board-champions')
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
          lastScrolledSector.current = currentSector
        }
      }, 100)
    }
  }, [searchParams, isLoadingProfiles, viewMode])

  const toggleType = (id: string) => {
    setOpenType(prev => prev === id ? null : id)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setCurrentPage(1) // Reset to first page on new search
      // Filter profiles based on search query
      const filtered = allProfiles.filter(profile =>
        profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.bio.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      setFilteredProfiles(filtered)
      setViewMode('profiles')
      updateUrl({ q: searchQuery, query: null, view: 'profiles', profile: null, page: null })
      setEmptyFacets(null)
    }
  }

  const handleSectorClick = async (sector: string) => {
    setSelectedSector(sector)
    setCurrentPage(1) // Reset to first page on new filter

    // Import sector mapping function dynamically to avoid client-side import issues
    const { profileMatchesSector } = await import('@/lib/config/sector-mappings')

    const filtered = allProfiles.filter(profile =>
      profileMatchesSector(profile.sectors, sector, profile.roles)
    )
    setFilteredProfiles(filtered)
    setViewMode('profiles')
    updateUrl({ sectors: sector.toLowerCase(), role: null, view: 'profiles', profile: null, page: null })
    setEmptyFacets(null)
    // On mobile, jump to results after filtering
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setTimeout(() => {
        const el = document.getElementById('results') || document.getElementById('find')
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 0)
    }
  }

  const handleRoleClick = async (role: string) => {
    setSelectedSector(role.toUpperCase())
    setCurrentPage(1) // Reset to first page on new filter

    // Use the same logic as URL filtering for consistency
    try {
      const { getBrowseTypeBySlug } = await import('@/src/data/browse-types')
      const { profileMatchesSector } = await import('@/lib/config/sector-mappings')
      const type = getBrowseTypeBySlug(role)

      if (type) {
        const roleTerms = (type.filters.role || []).map(r => r.toLowerCase())
        const sectorTerms = (type.filters.sectors || []).map(s => s.toLowerCase())

        const filtered = allProfiles.filter(profile => {
          let matchesRole = false
          let matchesSector = false

          if (roleTerms.length > 0) {
            const profileRoles = (profile.roles || []).map(r => r.toLowerCase())
            const titleLower = (profile.title || '').toLowerCase()
            matchesRole = roleTerms.some(term =>
              profileRoles.includes(term) || titleLower.includes(term)
            )
          }

          if (sectorTerms.length > 0) {
            matchesSector = sectorTerms.some(sector =>
              profileMatchesSector(profile.sectors, sector, profile.roles)
            )
          }

          if (roleTerms.length > 0 && sectorTerms.length > 0) {
            return matchesRole || matchesSector
          } else if (roleTerms.length > 0) {
            return matchesRole
          } else if (sectorTerms.length > 0) {
            return matchesSector
          }

          return true
        })

        setFilteredProfiles(filtered)
      } else {
        // Fallback for unmapped types
        const { profileMatchesTypeLabel } = await import('@/lib/config/sector-mappings')
        const filtered = allProfiles.filter(profile =>
          profileMatchesTypeLabel(profile, role)
        )
        setFilteredProfiles(filtered)
      }
    } catch {
      // Error fallback
      setFilteredProfiles([])
    }

    setViewMode('profiles')
    updateUrl({ role: role.toLowerCase(), sectors: null, view: 'profiles', profile: null, page: null })
    setEmptyFacets(null)
    
    // On mobile, jump to results after filtering
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setTimeout(() => {
        const el = document.getElementById('results') || document.getElementById('find')
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 0)
    }
  }

  const handleProfileClick = (profile: { id: string }) => {
    // Navigate to the dedicated profile page
    router.push(`/profiles/${profile.id}`)
  }

  const handleBackToResults = () => {
    setViewMode('profiles')
    setSelectedProfile(null)
    updateUrl({ profile: null, view: 'profiles' })
  }

  const handleBackToSectors = () => {
    setViewMode('sectors')
    setSelectedSector(null)
    setSelectedProfile(null)
    setFilteredProfiles(allProfiles)
    setSearchQuery("")
    setCurrentPage(1)
    updateUrl({ q: null, query: null, role: null, sectors: null, profile: null, view: 'sectors', page: null, sortBy: null })
  }

  const getRoleDescription = (role: string): React.ReactNode => {
    const descriptions: Record<string, React.ReactNode> = {
      // COO Types
      'Interim COO': (
        <>
          Our <strong>Interim Chief Operating Officers</strong> provide immediate leadership during transitions, 
          delivering operational excellence and strategic implementation. They specialise in rapid business transformation, 
          process optimization, and crisis management, typically engaging for 6-18 month assignments to drive critical change initiatives.
        </>
      ),
      'Fractional COO': (
        <>
          Our <strong>Fractional Chief Operating Officers</strong> offer part-time executive expertise for growing companies 
          that need senior operational leadership without full-time commitment. They provide strategic oversight, 
          implement scalable processes, and mentor internal teams while working 1-3 days per week.
        </>
      ),
      'COO Advisor': (
        <>
          Our <strong>COO Advisors</strong> bring decades of operational expertise to guide your leadership team through 
          complex challenges. They provide strategic counsel on operational efficiency, organizational design, 
          and performance improvement without day-to-day management responsibilities.
        </>
      ),
      
      // CFO Types
      'Interim CFO': (
        <>
          Our <strong>Interim Chief Financial Officers</strong> step in during critical financial transitions, 
          providing expert leadership for restructuring, M&A activities, or sudden departures. They ensure financial 
          stability and continuity while implementing robust financial controls and reporting systems.
        </>
      ),
      'Fractional CFO': (
        <>
          Our <strong>Fractional Chief Financial Officers</strong> provide strategic financial leadership on a part-time basis, 
          perfect for SMEs needing sophisticated financial expertise. They oversee financial planning, investor relations, 
          and strategic decision-making while optimizing your financial operations.
        </>
      ),
      'CFO Advisor': (
        <>
          Our <strong>CFO Advisors</strong> offer strategic financial counsel to boards and executive teams, 
          providing expertise in capital structure, financial strategy, and risk management. They guide major financial 
          decisions without operational involvement.
        </>
      ),
      
      // CMO Types
      'Interim CMO': (
        <>
          Our <strong>Interim Chief Marketing Officers</strong> drive marketing transformation and brand repositioning 
          during critical periods. They rapidly assess market opportunities, rebuild marketing functions, 
          and launch strategic campaigns while recruiting permanent leadership.
        </>
      ),
      'Fractional CMO': (
        <>
          Our <strong>Fractional Chief Marketing Officers</strong> provide strategic marketing leadership for companies 
          seeking to accelerate growth. They develop go-to-market strategies, oversee digital transformation, 
          and build high-performing marketing teams on a flexible schedule.
        </>
      ),
      'CMO Advisor': (
        <>
          Our <strong>CMO Advisors</strong> provide strategic marketing guidance to help companies navigate changing markets 
          and consumer behaviors. They offer expertise in brand strategy, digital marketing, and customer experience 
          without operational responsibilities.
        </>
      ),
      
      // Sector-based descriptions
      'engineering': (
        <>
          Our <strong>Engineering Professionals</strong> bring deep technical expertise and innovation leadership 
          to drive product development and technical excellence. From CTOs to Engineering Directors, they specialise 
          in building world-class engineering teams and delivering complex technical projects.
        </>
      ),
      'financial': (
        <>
          Our <strong>Financial Services Experts</strong> provide specialized knowledge in banking, insurance, and investment 
          management. They bring regulatory expertise, risk management capabilities, and strategic insight to help 
          navigate the complex financial services landscape.
        </>
      ),
      'technology': (
        <>
          Our <strong>Technology Leaders</strong> drive digital transformation and innovation across all industries. 
          They bring expertise in cloud computing, AI/ML, cybersecurity, and emerging technologies to help 
          organizations stay competitive in the digital age.
        </>
      ),
      'marketing': (
        <>
          Our <strong>Marketing Executives</strong> are brand builders and growth catalysts who understand modern 
          consumer behavior. They excel in digital marketing, brand strategy, and customer acquisition, 
          helping businesses connect with their audiences in meaningful ways.
        </>
      ),
      'human-resources': (
        <>
          Our <strong>Human Resources Leaders</strong> transform organizational culture and drive people strategies 
          that enable business success. They specialise in talent acquisition, employee engagement, 
          and building inclusive workplaces that attract and retain top talent.
        </>
      ),
      'legal': (
        <>
          Our <strong>Legal Professionals</strong> provide expert counsel on corporate governance, compliance, 
          and risk management. They help navigate complex regulatory environments, manage legal risks, 
          and ensure robust corporate governance practices.
        </>
      ),
      'ai': (
        <>
          Our <strong>AI Specialists</strong> are at the forefront of artificial intelligence and machine learning innovation. 
          They help organizations leverage AI for competitive advantage, from implementing predictive analytics 
          to developing intelligent automation solutions.
        </>
      ),
      'digital': (
        <>
          Our <strong>Digital Transformation Leaders</strong> guide organizations through comprehensive digital change. 
          They combine technology expertise with business acumen to reimagine business models, 
          customer experiences, and operational processes for the digital era.
        </>
      ),
      'communications': (
        <>
          Our <strong>Communications Executives</strong> are masters of narrative and reputation management. 
          They excel in crisis communications, stakeholder engagement, and building compelling brand stories 
          that resonate across all channels.
        </>
      ),
      'construction': (
        <>
          Our <strong>Construction Industry Leaders</strong> bring expertise in project management, sustainable building, 
          and infrastructure development. They understand the complexities of large-scale projects and deliver 
          results on time and within budget.
        </>
      ),
      'fmcg': (
        <>
          Our <strong>FMCG Professionals</strong> understand the fast-paced consumer goods market and excel in 
          supply chain optimization, brand management, and retail strategy. They drive growth through innovation 
          and operational excellence in competitive markets.
        </>
      ),
      'people': (
        <>
          Our <strong>People Leaders</strong> focus on creating high-performance cultures and engaged workforces. 
          They specialise in organizational development, change management, and building teams 
          that drive business success through people-first strategies.
        </>
      )
    }
    
    return descriptions[role] || descriptions[role.toLowerCase()] || (
      <>
        Our <strong>{role} Professionals</strong> bring specialized expertise and proven leadership to help 
        your organization achieve its strategic objectives. They combine deep industry knowledge with 
        practical experience to deliver measurable results.
      </>
    )
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  // Respond to URL param changes and initial load
  useEffect(() => {
    // Ensure we run after initial profiles load, but also handle profile param early
    if (!searchParams) return
    const params = new URLSearchParams(searchParams as any)
    // If profiles not loaded yet and no profile param, delay
    if (allProfiles.length === 0 && !(params.get('profile') || '')) {
      return
    }
    applyFiltersFromParams(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allProfiles])

  return (
    <main className="min-h-screen bg-[#ececec] text-[#444447]">
      {/* Navigation */}
      <Navbar />
      
      {/* Hero Banner with Full Awards Collage */}
      <section className="relative w-full">
        {/* Background Image Container - Responsive with aspect ratio */}
        <div className="relative w-full min-h-[420px] sm:min-h-[460px] md:min-h-[500px] lg:min-h-[540px] xl:min-h-[580px] 2xl:min-h-[620px] aspect-[16/9] sm:aspect-[21/9] md:aspect-[2.4/1] lg:aspect-[2.8/1] xl:aspect-[3/1]">
          <Image
            src="/assets/bc/awards/collage.png"
            alt="Award-Winning C-Suite & Board-Level Talent Agency - Over 70 awards received"
            fill
            className="object-cover object-center"
            priority
            fetchPriority="high"
            sizes="100vw"
            style={{ filter: 'none' }}
          />

          {/* Ultra-wide screen gradient overlay - ensures smooth hero→wave transition */}
          <div className="hidden 3xl:block absolute bottom-0 left-0 right-0 h-[20vh] bg-gradient-to-b from-transparent via-[#3E588F]/20 to-[#5A7DB8]/40 pointer-events-none" aria-hidden="true" />

          {/* Text Content - Positioned in blue area */}
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full h-full">
              {/* Text container - constrained to blue diagonal area */}
              <div className="absolute left-[4%] sm:left-[5%] md:left-[5%] lg:left-[4.5%] xl:left-[4%] top-[38%] sm:top-[42%] -translate-y-1/2 w-[88%] sm:w-[42%] md:w-[34%] lg:w-[30%] xl:w-[28%] 2xl:w-[26%] max-w-[540px]">
                {/* Dotted Arrow - Hidden on mobile */}
                <div className="mb-2 sm:mb-4 -ml-3 hidden sm:block">
                  <Image
                    src="/assets/bc/decor/dotted-arrow-down.svg"
                    alt=""
                    width={60}
                    height={60}
                    className="opacity-90"
                  />
                </div>

                {/* Mobile-only contrast panel behind text */}
                <div className="sm:hidden absolute -inset-3 rounded-2xl bg-black/55 backdrop-blur-[1px]" aria-hidden="true" />

                {/* Hero Text - Larger and high-contrast on mobile */}
                <div className="relative animate-fadeIn">
                  <h1
                    className="font-extrabold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] mb-4 sm:mb-[1.5vw] leading-[1.1] sm:leading-[1.05]"
                    style={{ fontSize: 'clamp(1.5rem, 4.5vw, 3.2rem)' }}
                  >
                    <span className="block">Board Champions</span>
                    <span className="block">Connects Ambitious Businesses</span>
                    <span className="block text-[#b9c8e6]">With Boardroom Leaders</span>
                  </h1>

                  {/* Search Now Button - Prominent on mobile */}
                  <a
                    href="#find"
                    className="inline-flex items-center rounded-full px-6 sm:px-[1.8vw] py-3 sm:py-[0.8vw] font-semibold tracking-wide uppercase text-white bg-[#7294c7] hover:bg-[#5d7cb8] focus:outline-none focus:ring-2 focus:ring-white/60 gap-2 sm:gap-[0.5vw] shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                    style={{ fontSize: 'clamp(0.8rem, 2vw, 0.95rem)' }}
                  >
                    SEARCH NOW
                    <svg className="w-4 h-4 sm:w-[1.2vw] sm:h-[1.2vw] min-w-[14px] min-h-[14px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Subtle divider to lead into credibility section */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#d1d5e0] to-transparent"></div>
      </section>

      {/* Why Board Champions Section with integrated wave */}
      <WhyBoardChampionsSection
        sectorSlug={searchParams?.get('sectors')?.split(',')[0] || searchParams?.get('role') || null}
      />

      {/* Standardized wave transition - Why Board Champions to main content */}
      <div style={{ backgroundColor: '#DFE9F4' }}>
        <WaveDivider
          position="bottom"
          color="section-fade"
          orientation="reverse"
          alignWithSection={false}
        />
      </div>

      {/* Main Content Section - Sky Gradient Fade background harmonizes with hero */}
      <section
        id="find"
        className="py-20 scroll-mt-[var(--header-h)]"
        style={{
          background: 'linear-gradient(to bottom, #DFE9F4 0%, #E3EDF7 20%, #F5F8FB 100%)',
          borderTop: '1px solid rgba(107, 155, 210, 0.08)', /* Subtle divider for hero continuity */
          boxShadow: '0 -1px 6px rgba(107, 155, 210, 0.12)' /* Separation glow for optical depth */
        }}
      >
        <div className="px-6 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-24 max-w-[1920px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 xl:gap-12 2xl:gap-16">
            
            {/* LEFT COLUMN: Browse by type */}
            <aside className="lg:col-span-4">
              <h2 className="h3 mb-6" style={{color: 'var(--body-color)'}}>Find your expert:</h2>
              
              {/* Search Bar */}
              {/* Search with Autocomplete */}
              <div className="mb-8">
                <SearchAutocomplete
                  placeholder="Search name, role, sector..."
                  onSearch={(query) => {
                    setSearchQuery(query)
                    updateUrl({ q: query, view: 'profiles' })
                  }}
                />
              </div>

              {/* Inline Expert CTA - subtle and on-brand */}
              <InlineJoinCTA size="small" />
              
              {/* Browse by Type - Single Blue Container */}
              <nav aria-labelledby="type-nav-title">
                <h3 id="type-nav-title" className="body font-semibold uppercase tracking-wide mb-3" style={{color: 'var(--body-color)'}}>
                  Browse by type:
                </h3>

                {/* SINGLE BLOCK CONTAINER */}
                <div className="rounded-3xl bg-[#8fa5d6] shadow-soft-lg overflow-hidden ring-1 ring-white/20">
                  <ul>
                    {[
                      { label: "CEO", href: "/browse/ceo" },
                      { label: "COO", href: "/browse/coo" },
                      { label: "CFO", href: "/browse/cfo" },
                      { label: "CMO", href: "/browse/cmo" },
                      { label: "CCO", href: "/browse/cco" },
                      { label: "CTO", href: "/browse/cto" },
                      { label: "CHRO", href: "/browse/chro" },
                      { label: "MANAGING DIRECTORS", href: "/browse/managing-directors" },
                      { label: "DIRECTORS", href: "/browse/directors" },
                      { label: "FRACTIONAL APPOINTMENTS", href: "/browse/fractional-employment" },
                      { label: "ADVISORY", href: "/browse/advisory" },
                      { label: "FINANCIAL", href: "/browse/financial" },
                      { label: "HUMAN RESOURCES", href: "/browse/human-resources" },
                      { label: "MARKETING", href: "/browse/marketing" },
                      { label: "TECHNICAL", href: "/browse/technical" },
                      { label: "DIGITAL", href: "/browse/digital" },
                      { label: "CREATIVE", href: "/browse/creative" },
                      { label: "PR", href: "/browse/pr" },
                      { label: "FUNDING", href: "/browse/funding" },
                      { label: "COMMERCIAL", href: "/browse/commercial" },
                      { label: "ENGINEERING", href: "/browse/engineering" },
                      { label: "CONSTRUCTION", href: "/browse/construction" },
                      { label: "AI", href: "/browse/ai" }
                    ].map((type, i, arr) => {
                      const id = type.label.toLowerCase().replace(/\s+/g, "-");
                      const isOpen = openType === id;

                      return (
                        <li key={id} className="relative">
                          {/* ROW - clickable everywhere */}
                          <button
                            type="button"
                            onClick={() => {
                              if (type.children?.length) {
                                toggleType(id)
                              } else {
                                // Extract role slug from href (e.g., '/browse/ceo' -> 'ceo')
                                const roleSlug = type.href.split('/').pop() || ''
                                handleRoleClick(roleSlug)
                              }
                            }}
                            className="w-full flex h-[52px] items-center justify-between px-5 text-white hover:bg-white/15 transition-all duration-300 group"
                            aria-expanded={isOpen}
                            aria-controls={`${id}-panel`}
                          >
                            {/* parent label */}
                            <span className="text-[15px] font-bold tracking-[0.08em] uppercase text-white group-hover:translate-x-1 transition-transform duration-300">
                              {type.label}
                            </span>

                            {/* chevron/arrow indicator */}
                            {type.children?.length ? (
                              <div className="text-white/80">
                                {isOpen ? (
                                  /* Down chevron when open */
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M6 9l6 6 6-6" />
                                  </svg>
                                ) : (
                                  /* Plus when closed */
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" />
                                  </svg>
                                )}
                              </div>
                            ) : (
                              /* Right arrow for direct navigation */
                              <svg
                                className="h-4 w-4 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            )}
                          </button>

                          {/* DOTTED SEPARATOR (skip last row and when children are visible immediately below) */}
                          <span
                            aria-hidden="true"
                            className={[
                              "absolute left-5 right-5 h-px bottom-0",
                              "bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.3)_0_2px,transparent_2px_6px)]",
                              i === arr.length - 1 ? "hidden" : "",
                              isOpen ? "hidden" : ""
                            ].join(" ")}
                          />

                          {/* CHILDREN REGION (same blue background with indented items) */}
                          {type.children?.length ? (
                            <div
                              id={`${id}-panel`}
                              role="region"
                              hidden={!isOpen}
                              className="bg-black/5"
                            >
                              <ul>
                                {type.children.map(c => (
                                  <li key={c.href} className="relative">
                                    <button
                                      onClick={() => {
                                        // Use role-family aware matching for clean results
                                        import('@/lib/config/sector-mappings').then(({ profileMatchesTypeLabel }) => {
                                          const filtered = allProfiles.filter(p => profileMatchesTypeLabel(p, c.label))
                                          setFilteredProfiles(filtered)
                                        })
                                        setViewMode('profiles')
                                        setSelectedSector(c.label)
                                        updateUrl({ role: slugify(c.label), sectors: null, view: 'profiles', profile: null })
                                      }}
                                      className="w-full flex h-[48px] items-center justify-between pl-12 pr-4 text-white hover:bg-white/10 transition-colors text-left"
                                    >
                                      <span className="text-[15px] font-bold tracking-[0.06em] uppercase text-white">
                                        {c.label}
                                      </span>
                                      <svg
                                        className="h-4 w-4 text-white/60"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path d="M9 18l6-6-6-6" />
                                      </svg>
                                    </button>
                                    {/* Dotted separator after each child item */}
                                    <span
                                      aria-hidden="true"
                                      className="absolute left-12 right-4 h-px bottom-0 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.3)_0_2px,transparent_2px_6px)]"
                                    />
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </nav>
            </aside>

            {/* RIGHT COLUMN: Main Content */}
            <div className="lg:col-span-8">
                {/* Dynamic Title based on view - only for profiles/profile-detail */}
                {viewMode !== 'sectors' && (
                  <h2 className="h2 text-balance text-center mb-6" style={{color: 'var(--body-color)'}}>
                    {viewMode === 'profiles' && (selectedSector ? `${selectedSector.charAt(0).toUpperCase() + selectedSector.slice(1)} Professionals` : 'Search Results')}
                    {viewMode === 'profile-detail' && selectedProfile?.name}
                  </h2>
                )}

                {/* Dynamic Description based on selected category */}
                {viewMode === 'profiles' && selectedSector && (
                  <div className="mb-8 text-center">
                    <p className="body max-w-[var(--measure)] mx-auto" style={{color: 'var(--body-color)'}}>
                      {getRoleDescription(selectedSector)}
                    </p>
                  </div>
                )}

                {/* Dynamic Content Based on View Mode */}
                {viewMode === 'sectors' && (
                  <>
                    <div className="mb-8 text-center">
                      <h2 className="text-[34px] md:text-[40px] font-bold text-balance mb-4" style={{color: 'var(--body-color)'}}>
                        Choose your required area of expertise:
                      </h2>
                      <p className="text-[16px] md:text-[17px] leading-relaxed mb-6" style={{color: 'var(--body-color)', opacity: 0.8}}>
                        Simplify your search for an advisor today by<br />
                        selecting from our wide range of sectors:
                      </p>
                    </div>

                    {/* 4×3 Sector Grid - Responsive with better scaling */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
                  {[
                    { label: "ENGINEERING", image: "/assets/bc/sectors/Engineering.jpeg", value: "engineering" },
                    { label: "FINANCIAL", image: "/assets/bc/sectors/financial.jpeg", value: "financial" },
                    { label: "CONSTRUCTION", image: "/assets/bc/sectors/Construction.jpeg", value: "construction" },
                    { label: "HUMAN RESOURCES", image: "/assets/bc/sectors/Humanresources.jpeg", value: "human-resources" },
                    { label: "AI", image: "/assets/bc/sectors/AI.webp", value: "ai" },
                    { label: "TECHNOLOGY", image: "/assets/bc/sectors/technology.jpeg", value: "technology" },
                    { label: "PEOPLE", image: "/assets/bc/sectors/Communication.webp", value: "people" },
                    { label: "MARKETING", image: "/assets/bc/sectors/Marketing.jpeg", value: "marketing" },
                    { label: "DIGITAL", image: "/assets/bc/sectors/Digital.webp", value: "digital" },
                    { label: "COMMUNICATIONS", image: "/assets/bc/sectors/Communications.jpeg", value: "communications" },
                    { label: "LEGAL", image: "/assets/bc/sectors/legal.jpeg", value: "legal" }
                  ].map((sector) => (
                    <button
                      key={sector.label}
                      onClick={() => handleSectorClick(sector.value)}
                      className="group relative block overflow-hidden rounded-2xl bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#7294c7] focus:ring-offset-2 cursor-pointer shadow-soft transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(107,155,210,0.4)] hover:scale-[1.02]"
                    >
                      <div className="relative aspect-square w-full">
                        <Image
                          src={sector.image}
                          alt={`${sector.label} sector specialists`}
                          fill
                          sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"
                          className="object-cover group-hover:scale-105"
                          style={{transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)'}}
                          loading="eager"
                          placeholder="blur"
                          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCwAA8A/9k="
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <span 
                        className="label-caps absolute bottom-3 left-3 drop-shadow-lg" 
                        style={{
                          color: 'white',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)'
                        }}
                      >
                        {sector.label}
                      </span>
                    </button>
                  ))}
                </div>
                  </>
                )}

                {/* Profiles Grid View */}
                {viewMode === 'profiles' && (
                  <div id="results" className="space-y-6">
                    {isLoadingProfiles && (
                      <>
                        {profilesViewMode === "grid" ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 items-stretch [grid-auto-rows:1fr]">
                            {Array.from({ length: PROFILES_PER_PAGE }).map((_, i) => (
                              <ProfileGridCardSkeleton key={i} className="h-full" />
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-8">
                            {Array.from({ length: PROFILES_PER_PAGE }).map((_, i) => (
                              <ProfileListCardSkeleton key={i} />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {profilesError && !isLoadingProfiles && (
                      <div className="text-center text-red-600">{profilesError}</div>
                    )}
                    {/* Empty state education */}
                    {!isLoadingProfiles && filteredProfiles.length === 0 && (
                      <div className="text-center border border-gray-200 rounded-2xl p-10 shadow-soft-md" style={{ backgroundColor: '#ececec' }}>
                        <h3 className="text-lg font-semibold mb-2" style={{color: 'var(--body-color)'}}>No results match your filters</h3>
                        <p className="text-gray-600 mb-4">Try one of these popular filters to get started:</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {loadingFacets && <span className="text-gray-500">Loading suggestions…</span>}
                          {!loadingFacets && emptyFacets && (
                            <>
                              {(emptyFacets.role || []).slice(0,4).map((r: any) => (
                                <button key={`r-${r.value}`} className="px-3 py-1 rounded-full border text-sm bg-[#ececec] hover:bg-[#e0e0e0]"
                                  onClick={() => {
                                    setSearchQuery(r.label)
                                    const f = allProfiles.filter(p => (p.roles||[]).some(x => x.toLowerCase().includes(r.label.toLowerCase())) || p.title.toLowerCase().includes(r.label.toLowerCase()))
                                    setFilteredProfiles(f)
                                    setViewMode('profiles')
                                  }}
                                >{r.label}</button>
                              ))}
                              {(emptyFacets.sectors || []).slice(0,6).map((s: any) => (
                                <button key={`s-${s.value}`} className="px-3 py-1 rounded-full border text-sm bg-[#ececec] hover:bg-[#e0e0e0]"
                                  onClick={() => handleSectorClick(s.value)}
                                >{s.label}</button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                  <button
                    onClick={handleBackToSectors}
                    className="text-[#7294c7] hover:underline flex items-center gap-2"
                  >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Sectors
                      </button>

                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Enhanced Results Counter */}
                    <span className="text-sm text-gray-600">
                      {!isLoadingProfiles && filteredProfiles.length > 0 && (
                        <>
                          Showing {((currentPage - 1) * PROFILES_PER_PAGE) + 1}-{Math.min(currentPage * PROFILES_PER_PAGE, totalProfiles || filteredProfiles.length)} of {totalProfiles || filteredProfiles.length} professionals
                        </>
                      )}
                      {!isLoadingProfiles && filteredProfiles.length === 0 && "No professionals found"}
                      {isLoadingProfiles && "Loading..."}
                    </span>

                    {/* Sorting Dropdown */}
                    {!isLoadingProfiles && filteredProfiles.length > 0 && (
                      <select
                        value={sortBy}
                        onChange={(e) => {
                          const newSortBy = e.target.value as 'recent' | 'experience' | 'relevance'
                          setSortBy(newSortBy)
                          setCurrentPage(1)
                          updateUrl({ sortBy: newSortBy, page: null })
                        }}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-[#ececec] text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#6b9bd2] focus:border-transparent"
                      >
                        <option value="recent">Most Recent</option>
                        <option value="experience">Most Experienced</option>
                        <option value="relevance">Most Relevant</option>
                      </select>
                    )}

                    <div className="flex gap-2 bg-gray-100 rounded-xl p-1.5 shadow-soft">
                      <Button
                        onClick={() => setProfilesViewMode("list")}
                        variant={profilesViewMode === "list" ? "default" : "ghost"}
                        size="sm"
                        className={profilesViewMode === "list" ? "bg-[#6b9bd2] hover:bg-[#5a82bd] text-white" : ""}
                      >
                        List View
                      </Button>
                      <Button
                        onClick={() => setProfilesViewMode("grid")}
                        variant={profilesViewMode === "grid" ? "default" : "ghost"}
                        size="sm"
                        className={profilesViewMode === "grid" ? "bg-[#6b9bd2] hover:bg-[#5a82bd] text-white" : ""}
                      >
                        Grid View
                      </Button>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      const nameParts = [] as string[]
                      if (selectedSector) nameParts.push(selectedSector)
                      if (searchQuery) nameParts.push(`“${searchQuery}”`)
                      const name = nameParts.length ? nameParts.join(' · ') : 'Saved search'
                      saved.addSearch(name, {
                        query: searchQuery,
                        role: [], sectors: selectedSector ? [selectedSector] : [], skills: [],
                        experience: '', location: '', availability: '', boardExperience: []
                      }, filteredProfiles.length)
                    }}
                    className="px-3 py-1.5 rounded-full border text-sm text-[#7294c7] border-[#7294c7] hover:bg-[#7294c7]/10"
                  >
                    Save search
                  </button>
                </div>

                    {!isLoadingProfiles && filteredProfiles.length > 0 && (() => {
                      // Detect if using API-level pagination (role or sector filters present)
                      const roleParam = searchParams?.get('role') || ''
                      const sectorsParam = searchParams?.get('sectors') || ''
                      const useApiPagination = !!(roleParam || sectorsParam)

                      // When using API pagination, results are already paginated server-side
                      // Only slice client-side for homepage without filters
                      const paginatedProfiles = useApiPagination
                        ? filteredProfiles  // Use API results directly (already correct page)
                        : filteredProfiles.slice(
                            (currentPage - 1) * PROFILES_PER_PAGE,
                            currentPage * PROFILES_PER_PAGE
                          )

                      return (
                        <>
                          {profilesViewMode === "grid" ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 items-stretch [grid-auto-rows:1fr]">
                              {paginatedProfiles.map((profile) => (
                                <ProfileGridCard
                                  key={profile.id}
                                  profile={profile}
                                  onView={(p) => handleProfileClick(p)}
                                  className="h-full"
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-8">
                              {paginatedProfiles.map((profile) => {
                                // Extract previous companies if available
                                const previousCompanies = profile.roles && profile.roles.length > 0
                                  ? profile.roles.slice(0, 3)
                                  : [];

                                return (
                                  <ProfileListCard
                                    key={profile.id}
                                    profile={{
                                      id: profile.id,
                                      name: profile.name || 'Executive',
                                      title: profile.title,
                                      bio: profile.bio,
                                      image: profile.image,
                                      imageUrl: profile.image,
                                      experience: profile.experience,
                                      location: profile.location,
                                      boardSeats: undefined,
                                      previousCompanies: previousCompanies.length > 0 ? previousCompanies : undefined,
                                      sectors: profile.sectors,
                                    }}
                                    onView={(p) => handleProfileClick(p)}
                                    // onContact not provided - will use modal by default
                                  />
                                );
                              })}
                            </div>
                          )}

                          {/* Pagination Controls */}
                          {(totalProfiles || filteredProfiles.length) > PROFILES_PER_PAGE && (
                            <div className="mt-12 flex justify-center">
                              <Pagination>
                                <PaginationContent>
                                  <PaginationItem>
                                    <PaginationPrevious
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        if (currentPage > 1) {
                                          const newPage = currentPage - 1
                                          setCurrentPage(newPage)
                                          updateUrl({ page: String(newPage) })
                                          // Scroll to top of results
                                          document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                        }
                                      }}
                                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                    />
                                  </PaginationItem>

                                  {(() => {
                                    const totalPages = Math.ceil((totalProfiles || filteredProfiles.length) / PROFILES_PER_PAGE)
                                    const pages = []

                                    // Always show first page
                                    pages.push(1)

                                    // Show pages around current page
                                    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                                      if (!pages.includes(i)) pages.push(i)
                                    }

                                    // Always show last page
                                    if (totalPages > 1 && !pages.includes(totalPages)) {
                                      pages.push(totalPages)
                                    }

                                    const items = []
                                    let lastPage = 0

                                    for (const page of pages) {
                                      // Add ellipsis if there's a gap
                                      if (page - lastPage > 1) {
                                        items.push(
                                          <PaginationItem key={`ellipsis-${page}`}>
                                            <PaginationEllipsis />
                                          </PaginationItem>
                                        )
                                      }

                                      items.push(
                                        <PaginationItem key={page}>
                                          <PaginationLink
                                            href="#"
                                            onClick={(e) => {
                                              e.preventDefault()
                                              setCurrentPage(page)
                                              updateUrl({ page: String(page) })
                                              document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                            }}
                                            isActive={currentPage === page}
                                            className="cursor-pointer"
                                          >
                                            {page}
                                          </PaginationLink>
                                        </PaginationItem>
                                      )

                                      lastPage = page
                                    }

                                    return items
                                  })()}

                                  <PaginationItem>
                                    <PaginationNext
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        const totalPages = Math.ceil((totalProfiles || filteredProfiles.length) / PROFILES_PER_PAGE)
                                        if (currentPage < totalPages) {
                                          const newPage = currentPage + 1
                                          setCurrentPage(newPage)
                                          updateUrl({ page: String(newPage) })
                                          document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                        }
                                      }}
                                      className={currentPage >= Math.ceil((totalProfiles || filteredProfiles.length) / PROFILES_PER_PAGE) ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                    />
                                  </PaginationItem>
                                </PaginationContent>
                              </Pagination>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    {/* Fetch facets when empty */}
                    {viewMode === 'profiles' && !isLoadingProfiles && filteredProfiles.length === 0 && !loadingFacets && !emptyFacets && (
                      <>
                        {(() => { setLoadingFacets(true); fetch('/api/search/filter-counts').then(r=>r.json()).then(d=>{ setEmptyFacets(d.data||d); }).finally(()=>setLoadingFacets(false)); })()}
                      </>
                    )}
                  </div>
                )}

                {/* Profile Detail View - Removed, now using dedicated /profiles/[id] route */}
                {false && viewMode === 'profile-detail' && selectedProfile && (
                  <div className="space-y-8">
                    <button
                      onClick={handleBackToResults}
                      className="text-[#7294c7] hover:underline flex items-center gap-2 mb-6"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to Results
                    </button>

                    <div className="rounded-2xl shadow-sm p-8" style={{ backgroundColor: '#ececec' }}>
                      <div className="grid md:grid-cols-3 gap-12">
                        <div className="md:col-span-1">
                          <div className="aspect-[3/4] bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl mb-6 relative overflow-hidden">
                            {/* Profile placeholder similar to cards */}
                            <div className="absolute inset-0 flex items-end justify-center pb-6">
                              <div className="w-full h-3/4 rounded-t-full bg-gradient-to-b from-gray-300/50 to-gray-400/30" />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-7xl font-light text-gray-400/50">
                                {selectedProfile.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="border-b pb-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Experience</p>
                              <p className="text-sm text-[#444447]">{selectedProfile.experience}</p>
                            </div>
                            <div className="border-b pb-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Location</p>
                              <p className="text-sm text-[#444447]">{selectedProfile.location}</p>
                            </div>
                            <div className="border-b pb-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Availability</p>
                              <p className="text-sm text-[#444447]">{selectedProfile.availability}</p>
                            </div>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <h1 className="text-3xl font-bold mb-2">{selectedProfile.name}</h1>
                          <p className="text-xl text-gray-600 mb-6">{selectedProfile.title}</p>
                          
                          <div className="mb-6">
                            <h3 className="font-semibold mb-2">Biography</h3>
                            <p className="text-gray-700">{selectedProfile.bio}</p>
                          </div>

                          <div className="mb-6">
                            <h3 className="font-semibold mb-2">Key Skills</h3>
                            <div className="flex flex-wrap gap-2">
                              {selectedProfile.skills.map((skill, index) => (
                                <span key={index} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <h3 className="font-semibold mb-2">Sectors</h3>
                            <div className="flex flex-wrap gap-2">
                              {selectedProfile.sectors.map((sector, index) => (
                                <span key={index} className="px-3 py-1 bg-[#7294c7]/10 text-[#7294c7] rounded-full text-sm">
                                  {sector.charAt(0).toUpperCase() + sector.slice(1)}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="flex gap-4 pt-6">
                            <button className="bg-[#8B9DC3] text-white px-8 py-3 rounded-full hover:bg-[#7186B3] transition-colors font-medium uppercase tracking-wider">
                              Book Call
                            </button>
                            <button className="border-2 border-[#8B9DC3] text-[#8B9DC3] px-8 py-3 rounded-full hover:bg-[#8B9DC3]/10 transition-colors font-medium uppercase tracking-wider">
                              Add to Shortlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Similar Professionals */}
                    <div className="mt-12">
                      <h3 className="text-xl font-semibold mb-6 text-center text-[#444447]">Similar Professionals to {selectedProfile.name.split(' ')[0]}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {allProfiles
                          .filter(p => p.id !== selectedProfile.id && p.sectors.some(s => selectedProfile.sectors.includes(s)))
                          .slice(0, 6)
                          .map((profile) => (
                            <button
                              key={profile.id}
                              onClick={() => handleProfileClick(profile)}
                              className="bg-[#ececec] rounded-xl shadow-sm p-4 hover:shadow-md transition-all text-center group"
                            >
                              <div className="aspect-square bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg mb-3 relative overflow-hidden">
                                <div className="absolute inset-0 flex items-end justify-center pb-2">
                                  <div className="w-full h-3/4 rounded-t-full bg-gradient-to-b from-gray-300/50 to-gray-400/30" />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-3xl font-light text-gray-400/50">
                                    {profile.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </div>
                                </div>
                              </div>
                              <h4 className="font-semibold text-sm text-[#444447] mb-1">{profile.name}</h4>
                              <p className="text-xs text-gray-500">{profile.title}</p>
                              <p className="text-xs text-[#8B9DC3] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View Profile →</p>
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </section>

      {/* Expert CTA Band (removed in favor of floating CTA for a lighter fit) */}

      {/* Client Logo Strip */}
      <section className="py-16 md:py-20 bg-[#f8fafc]" aria-live="off" style={{contain: 'layout style paint'}}>
        <div className="mx-auto max-w-7xl px-6">
          <h3 className="text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-center mb-12 text-neutral-500">
            TRUSTED BY INDUSTRY LEADERS
          </h3>

          <div
            className="relative overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
          >
            <div
              className={`flex space-x-16 md:space-x-24 ${
                !prefersReducedMotion && !isPaused
                  ? 'animate-scroll'
                  : ''
              }`}
              style={{ width: 'fit-content' }}
            >
              {/* First set of logos */}
              {bcManifest.logos.map((logo, index) => (
                <div
                  key={`first-${index}`}
                  className="flex-shrink-0 w-32 md:w-40 h-16 md:h-20 flex items-center justify-center"
                >
                  <img
                    src={logo.src}
                    alt={`${logo.name} logo`}
                    className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                    width="160"
                    height="80"
                    style={{ maxHeight: logo.scale ? `${60 * logo.scale}px` : '60px' }}
                  />
                </div>
              ))}

              {/* Duplicate set for seamless loop */}
              {bcManifest.logos.map((logo, index) => (
                <div
                  key={`second-${index}`}
                  className="flex-shrink-0 w-32 md:w-40 h-16 md:h-20 flex items-center justify-center"
                >
                  <img
                    src={logo.src}
                    alt={`${logo.name} logo`}
                    className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                    width="160"
                    height="80"
                    style={{ maxHeight: logo.scale ? `${60 * logo.scale}px` : '60px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <HomepageTestimonials />

      {/* Metrics Band (3 cards) */}
      <section className="py-16 bg-[#ececec]">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: "2,000+", label: "EXPERTS APPOINTED", icon: "/assets/bc/icons/2000-experts.svg" },
              { value: "70+", label: "AWARDS RECEIVED", icon: "/assets/bc/icons/70-awards.svg" },
              { value: "23", label: "YEARS IN BUSINESS", icon: "/assets/bc/icons/23-years.svg" }
            ].map((metric) => (
              <div
                key={metric.label}
                className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-[#7294c7]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img
                    src={metric.icon}
                    alt=""
                    width="32"
                    height="32"
                    className="opacity-70"
                    role="presentation"
                    aria-hidden="true"
                  />
                </div>
                
                {/* Value */}
                <div className="num-xl text-[#7294c7] mb-2">
                  {metric.value}
                </div>
                
                {/* Label */}
                <div className="label-caps">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave transition to footer */}
      <WaveDivider
        position="bottom"
        color="dark"
        orientation="normal"
        alignWithSection={false}
      />

      {/* Footer */}
      <Footer />

      {/* Floating CTA that appears on scroll */}
      <StickyJoinCTA size="medium" />
    </main>
  )
}

export default function Page() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7294c7] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    }>
      <HomePageContent />
    </Suspense>
  )
}
